import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import mixpanel from 'mixpanel-browser';
import { filter } from 'rxjs/operators';
import * as AuthenticatedTrackViewPost from '../../server/routes/orgs/track/view';
import * as LoggedOutTrackViewPost from '../../server/routes/track/view';
import { generatePath } from '../../server/services/utils.service';
import { environment } from '../environments';
import { AuthUserService } from './auth-user.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private router = inject(Router);
  private authUserService = inject(AuthUserService);
  private settingsService = inject(SettingsService);
  private http = inject(HttpClient);

  gtag: any = function () {
    // @ts-ignore
    // eslint-disable-next-line prefer-rest-params
    if (window.dataLayer) window.dataLayer.push(arguments);
  };
  fbqWrapped;
  fbq: any = function () {
    if (this.fbqWrapped) {
      // eslint-disable-next-line prefer-rest-params
      this.fbqWrapped(arguments);
    }
  };
  firstEverGoogleAnalyticsPageView = true;

  isProduction =
    window.location.href.startsWith('https://app.fociva.com') ||
    window.location.href.startsWith('https://fociva.com');

  userId?: string;
  orgId?: string;

  constructor() {
    this.authUserService.userOrgObj.subscribe((x) => {
      const userId = x?.user._id;
      if (userId) this.setUserId(userId);
      this.userId = userId;

      const orgId = x?.org._id;
      this.orgId = orgId;
    });

    // Subscribe to page changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.pageView();
      });

    // Load the analytics scripts.
    if (this.settingsService.getTrackingOption()) {
      // Mixpanel
      try {
        mixpanel.init(environment.mixpanel, {
          // debug: window.location.hostname !== 'fociva.com',
          track_pageview: true,
          persistence: 'localStorage',
        });
      } catch (err) {
        console.error('Mixpanel error');
        console.error(err);
      }

      // Google - we put this into the raw HTML file
      // try {
      //   // Add the gtag function into this component
      //   // @ts-ignore
      //   window.dataLayer = window.dataLayer || [];
      //   // gtag is created above
      //   // Initialize the gtag
      //   this.gtag('js', new Date());
      //   this.gtag('config', environment.googleAnalyticsTrackingId, {
      //     send_page_view: true,
      //   });

      //   const node = document.createElement('script');
      //   node.src =
      //     'https://www.googletagmanager.com/gtag/js?id=' +
      //     environment.googleAnalyticsTrackingId;
      //   node.type = 'text/javascript';
      //   node.async = true;
      //   document.getElementsByTagName('head')[0].appendChild(node);
      // } catch (err) {
      //   console.error('Google analytics error');
      //   console.error(err);
      // }

      // Facebook Pixel Code
      // Note, that the pixel load <noscript> is not included because Angular
      // cannot be used with noscript
      try {
        const node = document.createElement('script');
        node.async = true;
        node.innerHTML = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');`;
        document.getElementsByTagName('head')[0].appendChild(node);
        // @ts-ignore
        this.fbqWrapped = window.fbq;

        // Wait to get the userId (since we need to set it at init)
        setTimeout(() => {
          this.fbq('init', environment.facebookAppId, {
            external_id: this.userId,
          });
        }, 1000);
      } catch (err) {
        console.error('Facebook pixel error');
        console.error(err);
      }
    }
  }

  setUserId(userId: string) {
    if (this.settingsService.getTrackingOption()) {
      // Mixpanel
      mixpanel.identify(userId);

      // Google analytics
      this.gtag('set', { user_id: userId });

      // Facebook - set in 'init' above
    }
  }

  private pageView() {
    const url = window.location.href.split('?')[0]; // No query params to be sent
    const { title } = document;
    const referrer = document.referrer ? document.referrer : undefined;

    const urlObj = new URL(window.location.href);

    const body:
      | AuthenticatedTrackViewPost.ReqBody
      | LoggedOutTrackViewPost.ReqBody = { url, title, referrer };

    body.utm_campaign = urlObj.searchParams.get('utm_campaign') ?? undefined;
    body.utm_content = urlObj.searchParams.get('utm_content') ?? undefined;
    body.utm_medium = urlObj.searchParams.get('utm_medium') ?? undefined;
    body.utm_source = urlObj.searchParams.get('utm_source') ?? undefined;
    body.utm_term = urlObj.searchParams.get('utm_term') ?? undefined;

    body.dclid = urlObj.searchParams.get('dclid') ?? undefined;
    body.fbclid = urlObj.searchParams.get('fbclid') ?? undefined;
    body.gclid = urlObj.searchParams.get('gclid') ?? undefined;
    body.ko_click_id = urlObj.searchParams.get('ko_click_id') ?? undefined;
    body.li_fat_id = urlObj.searchParams.get('li_fat_id') ?? undefined;
    body.msclkid = urlObj.searchParams.get('msclkid') ?? undefined;
    body.ttclid = urlObj.searchParams.get('ttclid') ?? undefined;
    body.twclid = urlObj.searchParams.get('twclid') ?? undefined;
    body.wbraid = urlObj.searchParams.get('wbraid') ?? undefined;

    const trackPath = this.orgId
      ? generatePath<AuthenticatedTrackViewPost.ReqParams>(
          '/api/orgs/:orgId/track/view',
          { orgId: this.orgId }
        )
      : '/api/track/view';
    this.http
      .post<
        AuthenticatedTrackViewPost.ResBody | LoggedOutTrackViewPost.ResBody
      >(trackPath, body)
      .subscribe({
        next: () => {
          // Success
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  login(method: 'email' | 'facebook' | 'google') {
    if (this.settingsService.getTrackingOption()) {
      // Mixpanel
      mixpanel.track('Login', { method });

      // Google analytics
      this.gtag('event', 'login', { method }); // standard event

      // Facebook
    }
  }

  register(method: 'email' | 'facebook' | 'google') {
    if (this.settingsService.getTrackingOption()) {
      // Mixpanel
      mixpanel.people.set({ 'Register date': new Date().toISOString() });
      mixpanel.track('Register', { method });

      // Google analytics
      this.gtag('event', 'sign_up', { method }); // standard event

      this.gtag('event', 'conversion', {
        send_to: 'AW-860825828/bQpoCKn5uesZEOTRvJoD',
      });

      // Facebook
      this.fbq('track', 'CompleteRegistration');
    }
  }

  purchase({
    value,
    currency,
    transactionId,
  }: {
    value: number;
    currency: string;
    transactionId: string;
  }) {
    this.gtag('event', 'conversion', {
      send_to: 'AW-860825828/FvD1CPfJs-sZEOTRvJoD',
      value,
      currency: currency.toUpperCase(),
      transaction_id: transactionId,
      // event_callback: callback,
    });
  }

  mailingList() {
    if (this.settingsService.getTrackingOption()) {
      // Mixpanel
      mixpanel.track('Join email list');

      // Google analytics
      this.gtag('event', 'join_mailing_list');

      // Facebook
    }
  }
}
